<template>
  <div class="earworms-container">
    <Header :title="'Earworms'" :user="currentUser" />
    
    <main class="page-main">
      <div class="action-btn-bar">
        <button @click="createNewEarworm" class="action-btn construct-btn">New Earworm</button>
      </div>
      
      <div v-if="loading" class="loading">Loading earworms...</div>
      <div v-else-if="earworms.length === 0" class="empty-state">
        <p>No earworms found</p>
      </div>
      <div v-else class="tiles-container">
        <div
          v-for="earworm in earworms"
          :key="earworm.id"
          class="tile earworm-tile"
          @click="editEarworm(earworm.id)"
          style="cursor:pointer;"
        >
          <div class="earworm-header">
            <div class="earworm-image">
              <img v-if="earworm.image_url" :src="earworm.image_url" alt="Earworm Image" />
              <div v-else class="placeholder-image">
                <span>🎵</span>
              </div>
            </div>
            <div class="earworm-info">
              <h3>{{ earworm.name }}</h3>
              <p v-if="earworm.source" class="source">{{ earworm.source }}</p>
            </div>
          </div>
          
          <div class="earworm-actions">
            <button 
              @click.stop="editEarworm(earworm.id)" 
              class="action-btn edit-btn"
              title="Edit earworm"
            >
              ✏️ Edit
            </button>
            
            <!-- Play Button - shows only when audio is available -->
            <button 
              v-if="earworm.suno_audio_url || earworm.suno_audio_url2"
              @click.stop="playEarworm(earworm.id)" 
              class="action-btn play-btn"
              title="Play earworm with synchronized lyrics"
            >
              ▶️ Play
            </button>
            
            <!-- Show generating status -->
            <span 
              v-else-if="earworm.suno_task_status && ['pending', 'processing', 'generating'].includes(earworm.suno_task_status)"
              class="music-status generating"
              title="Music is being generated"
            >
              🎵 {{ earworm.suno_task_status }}...
            </span>
            
            <!-- Show no audio status -->
            <span 
              v-else
              class="music-status no-audio"
              title="No audio available"
            >
              🎶 No audio
            </span>
            
            <button 
              @click.stop="deleteEarworm(earworm.id)" 
              class="action-btn delete-btn"
              title="Delete earworm"
            >
              🗑️ Delete
            </button>
          </div>
          
          <!-- Single Line Lyrics Display -->
          <div 
            v-if="isPlaying(earworm.id) && earworm.suno_ts_lyrics" 
            class="lyrics-display"
            @click.stop
          >
            <div class="lyrics-line">
              <span 
                v-for="(word, index) in getCurrentLine(earworm.suno_ts_lyrics)" 
                :key="`${earworm.id}-${index}-${word.word}`"
                :class="{ 'current-word': isCurrentWord(word, currentTime) }"
                class="lyric-word"
              >
                {{ word.word }}
              </span>
              <div v-if="getCurrentLine(earworm.suno_ts_lyrics).length === 0" class="no-lyrics">
                No lyrics available at this time
              </div>
            </div>
          </div>
          
          <div class="earworm-meta">
            <small>Created: {{ formatDate(typeof earworm.created_at === 'string' ? earworm.created_at : (earworm.created_at ? earworm.created_at.toISOString() : '')) }}</small>
            <small v-if="earworm.suno_task_id">Task: {{ earworm.suno_task_id.substring(0, 8) }}...</small>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import Header from '../components/Header.vue'
import type { Earworm } from '../services/database'
import type { EarwormStatus } from '../services/database'

const router = useRouter()
const earworms = ref<Earworm[]>([])
const loading = ref(false)
const currentUser = ref<any>(null)

// Audio playback state
const currentAudio = ref<HTMLAudioElement | null>(null)
const currentlyPlayingId = ref<Earworm['id'] | null>(null)
const currentTime = ref<number>(0)
const currentWordIndex = ref<number>(-1)
const animationFrame = ref<number | null>(null)

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5000'

onMounted(async () => {
  // Load user data from sessionStorage
  try {
    const userStr = sessionStorage.getItem('currentUser')
    if (userStr) {
      currentUser.value = JSON.parse(userStr)
      console.log('Earworms: User loaded from sessionStorage:', currentUser.value)
    } else {
      console.log('Earworms: No user data in sessionStorage')
    }
  } catch (error) {
    console.error('Earworms: Error parsing user data from sessionStorage:', error)
  }
  
  console.log('Earworms: Loading without session validation')
  await loadEarworms()
})

const loadEarworms = async () => {
  loading.value = true
  try {
    const userStr = sessionStorage.getItem('currentUser')
    const userData = userStr ? JSON.parse(userStr) : null
    const sessionToken = userData?.sessionToken
    
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    }
    
    if (sessionToken) {
      headers['Authorization'] = `Bearer ${sessionToken}`
    }
    
    const url = `${API_BASE_URL}/api/earworms`
    
    const response = await fetch(url, { headers })
    if (!response.ok) {
      throw new Error(`Failed to load earworms: ${response.status} ${response.statusText}`)
    }
    earworms.value = (await response.json()) as Earworm[]
    console.log('Earworms: Loaded earworms from API:', earworms.value)
  } catch (error) {
    console.error('Failed to load earworms:', error)
  } finally {
    loading.value = false
  }
}

const createNewEarworm = async () => {
  try {
    const userStr = sessionStorage.getItem('currentUser')
    const userData = userStr ? JSON.parse(userStr) : null
    const sessionToken = userData?.sessionToken

    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    }
    if (sessionToken) {
      headers['Authorization'] = `Bearer ${sessionToken}`
    }

    // Only send required and valid fields for your schema
    const payload: Partial<Earworm> = {
      name: 'Untitled Earworm',
      user_id: userData?.id || null,
      analysis: null,
      suno_ts_lyrics: null,
      suno_ts_lyrics2: null,
      status: null
    }

    const response = await fetch(`${API_BASE_URL}/api/earworms`, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload)
    })

    if (!response.ok) {
      throw new Error('Failed to create new earworm')
    }

    const result = await response.json()
    router.push({ name: 'earworm-builder', params: { id: result.id } })
  } catch (error) {
    alert('Failed to create new earworm: ' + (error instanceof Error ? error.message : 'Unknown error'))
  }
}

const editEarworm = (id: Earworm['id']) => {
  const earwormId = String(id)
  console.log('✏️ Editing earworm with ID:', earwormId)
  if (!earwormId || earwormId === 'undefined' || earwormId === 'null') {
    console.error('❌ Invalid earworm ID for editing:', earwormId)
    return
  }
  router.push({ name: 'earworm-builder', params: { id: earwormId } })
}

const playEarworm = (id: Earworm['id']) => {
  const earwormId = String(id)
  console.log('🎵 Playing earworm with ID:', earwormId)
  if (!earwormId || earwormId === 'undefined' || earwormId === 'null') {
    console.error('❌ Invalid earworm ID for playing:', earwormId)
    return
  }
  
  router.push({ name: 'earworm-player', params: { id: earwormId } })
    .then(() => {
      console.log('✅ Navigation to earworm-player successful')
    })
    .catch((error) => {
      console.error('❌ Navigation to earworm-player failed:', error)
    })
}

const deleteEarworm = async (id: Earworm['id']) => {
  if (!confirm('Are you sure you want to delete this earworm?')) {
    return
  }
  
  try {
    const userStr = sessionStorage.getItem('currentUser')
    const userData = userStr ? JSON.parse(userStr) : null
    const sessionToken = userData?.sessionToken
    
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    }
    
    if (sessionToken) {
      headers['Authorization'] = `Bearer ${sessionToken}`
    }
    
    const response = await fetch(`${API_BASE_URL}/api/earworms/${id}`, {
      method: 'DELETE',
      headers
    })
    
    if (!response.ok) {
      throw new Error('Failed to delete earworm')
    }
    
    await loadEarworms() // Reload the list
  } catch (error) {
    console.error('Failed to delete earworm:', error)
    alert('Failed to delete earworm')
  }
}

const parsedLyrics = (tsLyricsData: Earworm['suno_ts_lyrics']) => {
  try {
    let tsLyrics
    
    // Check if the data is already an object or needs to be parsed
    if (typeof tsLyricsData === 'string') {
      tsLyrics = JSON.parse(tsLyricsData)
    } else if (typeof tsLyricsData === 'object' && tsLyricsData !== null) {
      tsLyrics = tsLyricsData
    } else {
      console.error('Invalid timestamped lyrics data type:', typeof tsLyricsData)
      return []
    }
    
    if (tsLyrics && tsLyrics.alignedWords && Array.isArray(tsLyrics.alignedWords)) {
      const parsed = tsLyrics.alignedWords
        .filter((item: any) => item.word && item.word.trim() && item.startS !== undefined && item.endS !== undefined) // Better filtering
        .filter((item: any) => {
          // Filter out song structure cues like (Verse), (Chorus), (Bridge), etc.
          const word = item.word.trim()
          const isStructureCue = /^\([^)]*\)/.test(word) || // Matches (Verse), (Chorus), etc.
                                /^(Verse|Chorus|Bridge|Intro|Outro|Pre-Chorus|Hook|Refrain)\s*\d*$/i.test(word) // Matches standalone structure words
          return !isStructureCue
        })
        .map((item: any) => ({
          word: item.word.trim(),
          start: Math.max(0, parseFloat(item.startS) || 0),  // Ensure positive values
          end: Math.max(0, parseFloat(item.endS) || 0)
        }))
        .filter((item: any) => item.start <= item.end) // Ensure valid time ranges
      
      console.log(`Parsed ${parsed.length} valid lyrics words (filtered out structure cues)`)
      return parsed
    }
  } catch (error) {
    console.error('Error parsing timestamped lyrics:', error)
    console.error('Data received:', tsLyricsData)
  }
  return []
}

const updateLyricsPosition = () => {
  if (!currentAudio.value || currentAudio.value.paused) return
  
  currentTime.value = currentAudio.value.currentTime
  
  // Find the current word based on audio time (with early highlighting)
  const currentEarworm = earworms.value.find(e => e.id === currentlyPlayingId.value)
  if (currentEarworm && currentEarworm.suno_ts_lyrics) {
    const lyrics = parsedLyrics(currentEarworm.suno_ts_lyrics)
    if (lyrics.length === 0) return
    
    const adjustedTime = currentTime.value + 0.15 // Slightly less offset for finding current word
    const newWordIndex = lyrics.findIndex((word: any) => 
      adjustedTime >= word.start && adjustedTime < word.end // Use < instead of <= to prevent overlap
    )
    
    // Update current word index
    if (newWordIndex >= 0 && newWordIndex !== currentWordIndex.value) {
      currentWordIndex.value = newWordIndex
      console.log(`Word ${newWordIndex}: "${lyrics[newWordIndex]?.word}" at time ${currentTime.value.toFixed(2)}s (adjusted: ${adjustedTime.toFixed(2)}s)`)
    }
  }
  
  // Continue animation only if still playing
  if (currentAudio.value && !currentAudio.value.paused) {
    animationFrame.value = requestAnimationFrame(updateLyricsPosition)
  }
}

const getCurrentLine = (tsLyricsData: Earworm['suno_ts_lyrics']) => {
  try {
    const lyrics = parsedLyrics(tsLyricsData)
    if (lyrics.length === 0) {
      console.warn('No lyrics found in ts_lyrics data')
      return []
    }
    
    // Find the current word index with early highlighting
    const adjustedTime = currentTime.value + 0.15
    const currentIndex = lyrics.findIndex((word: any) => 
      adjustedTime >= word.start && adjustedTime < word.end // Use < instead of <= for consistency
    )
    
    if (currentIndex === -1) {
      // If no current word, show the first few words or find the next upcoming word
      const nextWordIndex = lyrics.findIndex((word: any) => word.start > currentTime.value)
      if (nextWordIndex !== -1) {
        return getWordsAroundIndex(lyrics, nextWordIndex, 8)
      }
      return lyrics.slice(0, 8) // Show first 8 words
    }
    
    // Show words around the current word (current word in center)
    return getWordsAroundIndex(lyrics, currentIndex, 8)
  } catch (error) {
    console.error('Error in getCurrentLine:', error)
    return []
  }
}

const getWordsAroundIndex = (lyrics: any[], centerIndex: number, totalWords: number) => {
  const halfWords = Math.floor(totalWords / 2)
  const startIndex = Math.max(0, centerIndex - halfWords)
  const endIndex = Math.min(lyrics.length, startIndex + totalWords)
  
  return lyrics.slice(startIndex, endIndex)
}

const playAudio = (audioUrl: string, earwormId: Earworm['id']) => {
  // Stop any currently playing audio
  if (currentAudio.value) {
    currentAudio.value.pause()
    currentAudio.value = null
    currentlyPlayingId.value = null
  }

  const audio = new Audio(audioUrl)
  currentAudio.value = audio
  currentlyPlayingId.value = earwormId
  currentTime.value = 0
  currentWordIndex.value = -1 // Reset word index

  // Add event listeners
  audio.addEventListener('timeupdate', updateLyricsPosition)
  audio.addEventListener('ended', () => {
    currentAudio.value = null
    currentlyPlayingId.value = null
    currentTime.value = 0
    currentWordIndex.value = -1
    if (animationFrame.value) {
      cancelAnimationFrame(animationFrame.value)
    }
  })

  audio.addEventListener('error', (err) => {
    console.error('Failed to play audio:', err)
    alert('Failed to play audio')
    currentAudio.value = null
    currentlyPlayingId.value = null
    currentTime.value = 0
    currentWordIndex.value = -1
  })

  audio.play().catch(err => {
    console.error('Failed to play audio:', err)
    alert('Failed to play audio')
    currentAudio.value = null
    currentlyPlayingId.value = null
    currentTime.value = 0
    currentWordIndex.value = -1
  })
}

const pauseAudio = () => {
  if (currentAudio.value) {
    currentAudio.value.pause()
  }
}

const stopAudio = () => {
  if (currentAudio.value) {
    currentAudio.value.pause()
    currentAudio.value.currentTime = 0
    currentAudio.value = null
    currentlyPlayingId.value = null
    currentTime.value = 0
    currentWordIndex.value = -1
    if (animationFrame.value) {
      cancelAnimationFrame(animationFrame.value)
    }
  }
}

const isPlaying = (earwormId: Earworm['id']) => {
  return currentlyPlayingId.value === earwormId && 
         currentAudio.value && 
         !currentAudio.value.paused
}

const isCurrentWord = (word: any, currentTime: number) => {
  // Add a small offset to highlight words slightly earlier (0.2 seconds ahead)
  const adjustedTime = currentTime + 0.2
  return adjustedTime >= word.start && adjustedTime < word.end // Use < instead of <= for consistency
}

// Cleanup on component unmount
onUnmounted(() => {
  if (currentAudio.value) {
    currentAudio.value.pause()
    currentAudio.value = null
  }
  if (animationFrame.value) {
    cancelAnimationFrame(animationFrame.value)
  }
})

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString()
}
</script>

<style scoped>
.earworms-container {
  min-height: 100vh;
  background: #f8f9fa;
}

.page-main {
  padding: 1rem;
  max-width: 1200px;
  margin: 0 auto;
}

.action-btn-bar {
  display: flex;
  justify-content: flex-start;
  margin-bottom: 2rem;
}

.action-btn {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.construct-btn {
  background: #43a047;
  color: white;
}

.construct-btn:hover {
  background: #388e3c;
}

.loading {
  text-align: center;
  padding: 2rem;
  font-size: 1.1rem;
  color: #666;
}

.empty-state {
  text-align: center;
  padding: 3rem;
  color: #666;
}

.empty-state p {
  font-size: 1.2rem;
  margin-bottom: 1rem;
}

.tiles-container {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
}

.earworm-tile {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  transition: transform 0.2s, box-shadow 0.2s;
}

.earworm-tile:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(0,0,0,0.15);
}

.earworm-header {
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
}

.earworm-image {
  width: 60px;
  height: 60px;
  border-radius: 8px;
  overflow: hidden;
  flex-shrink: 0;
}

.earworm-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.placeholder-image {
  width: 100%;
  height: 100%;
  background: #f0f0f0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
}

.earworm-info {
  flex: 1;
}

.earworm-info h3 {
  margin: 0 0 0.5rem 0;
  font-size: 1.2rem;
  color: #333;
}

.source {
  margin: 0 0 0.5rem 0;
  color: #666;
  font-size: 0.9rem;
}

.earworm-actions {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
}

.audio-controls {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.play-btn, .pause-btn, .stop-btn {
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: background-color 0.2s;
}

.play-btn {
  background: #2196f3;
  color: white;
}

.play-btn:hover {
  background: #1976d2;
}

.music-status {
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-size: 0.85rem;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  text-transform: capitalize;
}

.music-status.generating {
  background: rgba(255, 152, 0, 0.1);
  color: #ff9800;
  border: 1px solid rgba(255, 152, 0, 0.3);
}

.music-status.no-audio {
  background: rgba(156, 156, 156, 0.1);
  color: #999;
  border: 1px solid rgba(156, 156, 156, 0.3);
}

.lyrics-display {
  position: relative;
  height: 50px;
  background: #f8f9fa;
  border-radius: 8px;
  margin-top: 1rem;
  border: 1px solid #e9ecef;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 0 1rem;
}

.lyrics-line {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
  text-align: center;
  line-height: 1.4;
}

.lyric-word {
  font-size: 0.95rem;
  color: #666;
  transition: all 0.4s ease;
  padding: 0.2rem 0.4rem;
  border-radius: 4px;
}

.lyric-word.current-word {
  color: #2196f3;
  font-weight: bold;
  font-size: 1.2rem;
  background: rgba(33, 150, 243, 0.1);
  text-shadow: 0 0 3px rgba(33, 150, 243, 0.3);
  transform: scale(1.1);
  border: 2px solid rgba(33, 150, 243, 0.3);
}

.no-lyrics {
  color: #999;
  font-style: italic;
  font-size: 0.9rem;
}

.earworm-meta {
  display: flex;
  justify-content: space-between;
  color: #999;
  font-size: 0.8rem;
}
</style>